import {
  ChipProps, Chip as MuiChip, styled, Tooltip, type SxProps, type Theme,
} from '@mui/material';
import { ReactNode, type JSX } from 'react';
import CustomTypography from './Typography';

interface IProps extends ChipProps {
  label: ReactNode;
  maxWidth?: string;
  colour?: string;
  backgroundColour?: string;
  border?: string;
  fontWeight?: 'light' | 'regular' | 'medium' | 'bold';
  tooltipText?: NonNullable<ReactNode>;
  pill?: boolean;
  wrapLabel?: boolean;
}

const Chip = styled(MuiChip)<IProps>(({
  theme,
  colour,
  backgroundColour,
  maxWidth,
  pill,
  border,
}) => ({
  borderRadius: pill ? '16px' : '4px',
  color: colour || theme.colours.chips.chipBlueText,
  backgroundColor: backgroundColour || theme.colours.chips.chipBlueBg,
  border,
  maxWidth,
  '& .MuiChip-label': {
    padding: pill ? '2px 10px !important' : 'inherit',
  },
}));

const wrapLabelSx: SxProps<Theme> = {
  maxWidth: '100%',
  height: 'auto',
  alignSelf: 'flex-start',
  '& .MuiChip-label': {
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflow: 'visible',
    textOverflow: 'clip',
    display: 'block',
  },
};

export default function CustomChip({
  label,
  maxWidth,
  colour,
  backgroundColour,
  size = 'small',
  fontWeight = 'medium',
  tooltipText = '',
  pill = false,
  wrapLabel = false,
  sx,
  ...props
}: IProps): JSX.Element {
  return (
    <Tooltip title={tooltipText}>
      <Chip
        colour={colour}
        backgroundColour={backgroundColour}
        maxWidth={maxWidth}
        size={size}
        pill={pill}
        {...props}
        sx={
          wrapLabel
            ? ([wrapLabelSx, sx].filter((v) => v != null) as SxProps<Theme>)
            : sx
        }
        label={(
          <CustomTypography
            variant="bodySmall"
            fontWeight={fontWeight}
            truncate={!wrapLabel}
            style={{
              color: colour,
            }}
          >
            {label}
          </CustomTypography>
        )}
      />
    </Tooltip>
  );
}
